import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Headphones, MessageCircle } from 'lucide-react';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConsultationModal: React.FC<ConsultationModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  const openGoftinoChat = () => {
    try {
      const w = window as any;
      if (w?.Goftino && typeof w.Goftino.open === 'function') {
        w.Goftino.open();
        return;
      }
      if (w?.Goftino && typeof w.Goftino.toggle === 'function') {
        w.Goftino.toggle();
        return;
      }
    } catch {
      // fallback
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/60 transition-opacity animate-fadeIn"
        style={{ zIndex: 'var(--z-overlay-consultation-modal, 10050)' }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 overflow-y-auto overscroll-contain"
        style={{ zIndex: 'calc(var(--z-overlay-consultation-modal, 10050) + 1)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="consultation-modal-title"
      >
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <div
            className="relative w-full max-w-md overflow-hidden animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{
              borderRadius: 24,
              background: 'linear-gradient(145deg, rgba(30, 35, 50, 0.92) 0%, rgba(20, 24, 38, 0.96) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.05) inset',
            }}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 left-3 rounded-full w-9 h-9 flex items-center justify-center transition-all hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
              style={{ zIndex: 2 }}
              aria-label="بستن"
            >
              <X size={18} className="text-white/50 hover:text-white/80" />
            </button>

            {/* Decorative gradient orb */}
            <div
              className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, transparent 70%)',
                filter: 'blur(20px)',
              }}
              aria-hidden="true"
            />

            {/* Content */}
            <div className="relative px-6 sm:px-8 pt-8 pb-6 sm:pb-8 text-center" style={{ zIndex: 1 }}>
              {/* Icon */}
              <div
                className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0.05) 100%)',
                  border: '1px solid rgba(6, 182, 212, 0.2)',
                }}
              >
                <Headphones size={30} className="text-[#06B6D4]" strokeWidth={1.8} />
              </div>

              {/* Title */}
              <h2
                id="consultation-modal-title"
                className="text-xl sm:text-2xl font-bold mb-3"
                style={{ color: '#4ADE80' }}
              >
                مشاوره تخصصی اینسایت
              </h2>

              {/* Description */}
              <p
                className="text-sm sm:text-base leading-relaxed mb-6"
                style={{ color: 'rgba(255, 255, 255, 0.65)' }}
              >
                اگر علاقمند به دریافت مشاوره درباره گزارش‌ها و داشبوردهای نگاه هوشمند هستید،
                همین الان از طریق چت آنلاین با تیم ما در ارتباط باشید.
              </p>

              {/* Features */}
              <div
                className="flex flex-col gap-2.5 mb-7 text-right"
                style={{ color: 'rgba(255, 255, 255, 0.55)' }}
              >
                {[
                  'راهنمایی در تفسیر گزارش‌ها و نمودارها',
                  'پیشنهاد بهترین داشبورد متناسب با نیاز شما',
                  'پاسخ به سوالات درباره عملکرد شعبه',
                ].map((text) => (
                  <div key={text} className="flex items-start gap-2.5 text-xs sm:text-sm leading-relaxed">
                    <span
                      className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: '#06B6D4' }}
                    />
                    {text}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                type="button"
                onClick={() => {
                  openGoftinoChat();
                  onClose();
                }}
                className="w-full flex items-center justify-center gap-2.5 rounded-xl px-6 py-3.5 font-semibold text-white text-sm sm:text-base transition-all duration-200 hover:brightness-110 hover:shadow-lg active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
                  boxShadow: '0 4px 20px rgba(6, 182, 212, 0.35)',
                }}
              >
                <MessageCircle size={20} strokeWidth={2} />
                شروع گفتگو با مشاور
              </button>

              {/* Dismiss link */}
              <button
                type="button"
                onClick={onClose}
                className="mt-4 text-xs transition-colors"
                style={{ color: 'rgba(255, 255, 255, 0.35)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.35)')}
              >
                بعدا یادآوری کن
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default ConsultationModal;
